import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../contexts/config-context';
import UserAPI from '../../axios/user';
import ListingAPI from '../../axios/listing';
import FileAPI from '../../axios/file';
import LocationAPI from '../../axios/locations';
import MiscAPI from '../../axios/misc';
import { formatNumber, PRICE_UNITS } from '../main-page/home-utils';
import { CategoryIcon, sortTypes } from '../new-listing/listing-tips';
import { youtubeId } from '../listing-detail/youtube';
import { Check, ChipGroup, Confirm, Dropdown, Field, Section } from './components/form-ui';
import { LocationPicker } from './components/location-picker';
import '../../hs-theme.css';
import '../new-listing/new-listing.css';

// the map starts here; a listing still at this spot has no location marked
const DEFAULT_POSITION = [44.8125, 20.4612];
// how close the map moves in on a location picked from the list (building numbers show from 17), and on a part of the city
const LOCATION_ZOOM = 17;
const AREA_ZOOM = 13;
const MAX_PHOTOS = 20;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
// iPhone photos are HEIC; some browsers leave their type empty, so the extension is checked too
const PHOTO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
const isPhoto = (file) => PHOTO_TYPES.includes(file.type) || PHOTO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
const isHeic = (file) => /^image\/hei[cf]$/.test(file.type) || /\.hei[cf]$/i.test(file.name);
// only Safari can show HEIC, so other browsers get a JPEG copy for the preview; the original file is still
// what gets uploaded, the server converts it. The converter is large, so it is loaded only when needed
const previewUrl = async (file) => {
    if (!isHeic(file)) return URL.createObjectURL(file);
    const { default: heic2any } = await import('heic2any');
    const jpeg = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.7 });
    return URL.createObjectURL(Array.isArray(jpeg) ? jpeg[0] : jpeg);
};
const MIN_DESCRIPTION = 40;
// [key, title in the "listing" texts, how much the section matters]
const SECTIONS = [
    ['basic', 'basic', 'required'],
    ['map', 'map_short', 'required'],
    ['photos', 'photos', 'recommended'],
    ['data', 'data', 'required'],
    ['features', 'features', 'optional'],
    ['description', 'description', 'recommended'],
    ['contact', 'contact', 'required'],
    // only a new listing picks a plan
    ['plan', 'plan_title', 'required'],
];

// in the order of the listing group ids on the server: 1 is standard, 2 is premium
const PLANS = ['standard', 'premium'];

const EMPTY_LISTING = Object.freeze({
    title: "", price: "", place: "", subplace: "", location: "", address: "", area: "",
    num_rooms: null, num_bathrooms: null, floor: null, max_floor: null, bills: "", deposit: "",
    property_type: 0, property_state: 0, heating: [],
    urgent: false, habitable: false, registered: false, vat: false, salon: false, duplex: false, mortgage: false,
    not_house: false, not_last_floor: false, attic: false, energy_passport: false, exchange: false,
    terrace: false, balcony: false, lift: false, basement: false, hot_water: false, has_phone: false, internet: false,
    cable: false, ac: false, interphone: false, surveillance: false, garage: false, parking: false,
    description: "", video_url: "", phone: "", mobile: "", email: "",
    lnglat: DEFAULT_POSITION,
});

const isEmpty = (value) => value === "" || value === null || value === undefined;
const isNumber = (value) => !isEmpty(value) && !isNaN(Number(value));
const digitsOnly = (value) => String(value || '').replace(/\D/g, '');
const emailOk =(value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
);

const RotateIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" /></svg>
);

// a not yet uploaded photo turned by 0, 90, 180 or 270 degrees inside a box of the given width / height ratio;
// turned sideways it is sized to the swapped box so it still covers it
const RotatedPhoto = ({ url, rotation, ratio }) => {
    const sideways = rotation % 180 !== 0;
    return (
        <span className='hs-nl-rotated' style={{
            backgroundImage: url ? `url(${url})` : undefined,
            width: sideways ? `${100 / ratio}%` : '100%',
            height: sideways ? `${100 * ratio}%` : '100%',
            transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        }}></span>
    );
};

const CameraIcon =({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><circle cx="12" cy="13" r="3.5" /></svg>
);

export const NewListingData = ({ type }) => {
    const { id: listingID } = useParams();
    const addMode = !listingID;
    // the property type picked on the category page
    const [searchParams] = useSearchParams();
    const pickedType = Number(searchParams.get('vrsta')) || 0;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation(["listing", "profile", "common", "home", "new_listing"]);
    const lang = i18n.language;
    const {
        api, listingType, floor_options, max_floor_options, num_rooms_options, num_bathrooms_options,
        checkboxItems, checkboxItemsRestData
    } = useContext(ConfigContext);
    const photoInput = useRef(null);

    const [form, setForm] = useState({ ...EMPTY_LISTING, listing_type: type, property_type: pickedType });
    const [place, setPlace] = useState(null);
    const [subplace, setSubplace] = useState(null);
    const [location, setLocation] = useState(null);
    const [places, setPlaces] = useState([]);
    const [subplaces, setSubplaces] = useState([]);
    const [locations, setLocations] = useState([]);
    const [options, setOptions] = useState({ types: [], states: [], heating: [], userTypes: [] });
    const [userType, setUserType] = useState(null);
    const [mapFocus, setMapFocus] = useState(DEFAULT_POSITION);
    const [focusZoom, setFocusZoom] = useState(LOCATION_ZOOM);
    // the gallery in its order, the cover first: photos the listing already has on the server
    // ({ uploaded, id, photo }) mixed with ones picked here ({ file, url, rotation })
    const [photos, setPhotos] = useState([]);
    const [dragging, setDragging] = useState(false);
    // the photo being dragged to a new place; the ref is read by the drop zone handlers
    const movingKey = useRef(null);
    const [moving, setMoving] = useState(null);
    // the plan the new listing is published with, and what each one costs
    const [plan, setPlan] = useState('standard');
    const [planPrices, setPlanPrices] = useState({ standard: 0, premium: 0 });
    // a standard listing is free while the user has no active one; unknown until the server answers
    const [freeStandard, setFreeStandard] = useState(false);
    // field errors stay hidden until the first attempt to publish
    const [tried, setTried] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
    const setNumber = (key) => (e) => set(key, e.target.value.replace(',', '.').replace(/[^\d.]/g, ''));
    const setText = (key) => (e) => set(key, e.target.value);
    const setDigits = (key) => (e) => set(key, digitsOnly(e.target.value));

    const populateListing = (data) => {
        // videos are no longer uploaded; the form only keeps the YouTube link
        const { photos, videos: legacyVideos, place: p, subplace: sp, location: loc, ...rest } = data;
        const position = [Number(data.lat), Number(data.lng)];
        setPlace(p);
        setSubplace(sp);
        setLocation(loc);
        setPhotos(photos.map(photo => ({ key: `uploaded-${photo.id}`, uploaded: true, id: photo.id, photo: photo.photo.ratios.null.sources['image/webp']['300'] })));
        setMapFocus(position);
        setForm(prev => ({
            ...prev, ...rest,
            place: p.id, subplace: sp.id, location: loc.id,
            price: parseFloat(rest.price) === 0 ? "" : rest.price,
            lnglat: position,
        }));
    };

    useEffect(() => {
        PLANS.forEach((key, i) => {
            ListingAPI.getPromotionPrice(i + 1).then(res => setPlanPrices(prev => ({ ...prev, [key]: res.data.price }))).catch(err => console.log(err));
        });
        LocationAPI.getListOfPlaces().then(res => setPlaces(res.data)).catch(err => console.log(err));

        if (addMode) {
            ListingAPI.getActiveListings(1, '-date_created', lang).then(res => setFreeStandard(res.data.count === 0)).catch(err => console.log(err));
            UserAPI.getProfileData().then(res => {
                setUserType(res.data.user_type);
                setForm(prev => ({ ...prev, email: res.data.email, phone: digitsOnly(res.data.phone), mobile: digitsOnly(res.data.mobile) }));
            }).catch(err => console.log(err));
        } else {
            ListingAPI.getListingData(listingID).then(res => populateListing(res.data)).catch(err => console.log(err));
            setUserType(JSON.parse(localStorage.getItem("user"))?.user_type);
        }
    }, []);

    // the three category routes share this component, so the type can change under it
    useEffect(() => {
        if (addMode) setForm(prev => ({ ...prev, listing_type: type, property_type: pickedType }));
    }, [type, pickedType]);

    useEffect(() => {
        Promise.all([
            MiscAPI.getPropertyTypeOptions(lang),
            MiscAPI.getPropertyStateOptions(lang),
            MiscAPI.getHeatingOptions(lang),
            MiscAPI.getUserTypeOptions(lang),
        ]).then(([types, states, heating, userTypes]) => setOptions({
            types: types.data, states: states.data, heating: heating.data, userTypes: userTypes.data,
        })).catch(err => console.error("Error fetching data:", err));
    }, [lang]);

    useEffect(() => {
        if (!place) return;
        LocationAPI.getListOfSubplaces(place.id).then(res => setSubplaces(res.data)).catch(err => console.log(err));
    }, [place?.id]);

    useEffect(() => {
        if (!subplace) return;
        LocationAPI.getListOfLocations(subplace.id).then(res => setLocations(res.data)).catch(err => console.log(err));
    }, [subplace?.id]);

    // picking a city or a part of it clears what was chosen below it
    const pickPlace = (picked) => {
        setPlace(picked);
        setSubplace(null);
        setLocation(null);
        setSubplaces([]);
        setLocations([]);
        setForm(prev => ({ ...prev, place: picked.id, subplace: "", location: "" }));
    };
    const pickSubplace = (picked) => {
        setSubplace(picked);
        setLocation(null);
        setLocations([]);
        setForm(prev => ({ ...prev, subplace: picked.id, location: "" }));
    };
    const pickLocation = (picked) => {
        setLocation(picked);
        set('location', picked.id);
        LocationAPI.getLocationCoordinates(picked.id)
            .then(res => {
                // when only the part of the city was found, the map stays further out
                setFocusZoom(res.data.exact ? LOCATION_ZOOM : AREA_ZOOM);
                setMapFocus([Number(res.data.lat), Number(res.data.lng)]);
            })
            .catch(err => console.log(err));
    };

    const photoCount = photos.length;
    const addPhotos = (files) => {
        setDragging(false);
        const picked = Array.from(files || []).filter(isPhoto).slice(0, MAX_PHOTOS - photoCount);
        if (picked.length === 0) return;
        const added = picked.map((file, i) => ({ key: `${Date.now()}-${i}-${file.name}`, file, url: null, rotation: 0 }));
        setPhotos(current => [...current, ...added]);
        // the tiles show up right away, each preview fills in once it is ready
        added.forEach(image => previewUrl(image.file)
            .then(url => setPhotos(current => {
                if (!current.some(other => other.key === image.key)) {
                    URL.revokeObjectURL(url);
                    return current;
                }
                return current.map(other => other.key === image.key ? { ...other, url } : other);
            }))
            .catch(err => console.log(err)));
    };
    // the turn is only previewed here; the server applies it when the photo is uploaded
    const rotatePhoto = (image) => {
        setPhotos(current => current.map(other => other.key === image.key ? { ...other, rotation: (other.rotation + 90) % 360 } : other));
    };
    const removePhoto = (image) => {
        if (image.uploaded) {
            FileAPI.removeImage(image.id).then(() => setPhotos(current => current.filter(other => other.key !== image.key))).catch(err => console.log(err));
            return;
        }
        URL.revokeObjectURL(image.url);
        setPhotos(current => current.filter(other => other.key !== image.key));
    };
    const deleteAllPhotos = () => {
        photos.forEach(image => !image.uploaded && URL.revokeObjectURL(image.url));
        setPhotos(current => current.filter(image => image.uploaded));
        if (!addMode) {
            FileAPI.removeAllImages(listingID).then(() => setPhotos(current => current.filter(image => !image.uploaded))).catch(err => console.log(err));
        }
        setDialog(null);
    };

    // the dragged photo takes the place of the one it is moved over
    const movePhoto = (targetKey) => {
        setPhotos(current => {
            const from = current.findIndex(image => image.key === movingKey.current);
            const to = current.findIndex(image => image.key === targetKey);
            if (from < 0 || to < 0 || from === to) return current;
            const next = [...current];
            next.splice(to, 0, ...next.splice(from, 1));
            return next;
        });
    };
    const photoDragProps = (image) => ({
        draggable: true,
        onDragStart: (e) => {
            movingKey.current = image.key;
            setMoving(image.key);
            e.dataTransfer.effectAllowed = 'move';
            // Firefox does not start a drag without data
            e.dataTransfer.setData('text/plain', image.key);
        },
        onDragEnter: () => movingKey.current && movePhoto(image.key),
        onDragEnd: () => {
            movingKey.current = null;
            setMoving(null);
        },
    });

    // files dropped from the computer are added; a photo moved inside the gallery is not a file drop
    const dragProps = {
        onDragOver: (e) => {
            e.preventDefault();
            if (movingKey.current) e.dataTransfer.dropEffect = 'move';
            else if (!dragging) setDragging(true);
        },
        onDragLeave: () => setDragging(false),
        onDrop: (e) => {
            e.preventDefault();
            if (!movingKey.current) addPhotos(e.dataTransfer.files);
        },
    };

    const videoOk = !String(form.video_url || '').trim() || !!youtubeId(form.video_url);
    const hasPin = form.lnglat[0] !== DEFAULT_POSITION[0] || form.lnglat[1] !== DEFAULT_POSITION[1];
    const optionalNumberOk = (value) => isEmpty(value) || isNumber(value);
    const numbersOk = isNumber(form.area) && optionalNumberOk(form.price) && optionalNumberOk(form.bills) && optionalNumberOk(form.deposit);
    const features = [...checkboxItems, ...checkboxItemsRestData];
    const status = {
        basic: !!(form.title.trim() && form.place && form.subplace && form.location && optionalNumberOk(form.price)),
        map: hasPin,
        photos: photoCount > 0,
        data: !!(isNumber(form.area) && form.num_rooms && form.num_bathrooms && Number(form.property_type) && Number(form.property_state)
            && form.floor && form.max_floor && optionalNumberOk(form.bills) && optionalNumberOk(form.deposit)),
        features: features.some(item => form[item.name]),
        description: (form.description || '').trim().length >= MIN_DESCRIPTION,
        contact: !!(String(form.phone || '').trim() && emailOk(form.email)),
        // one of the plans is always picked
        plan: true,
    };
    const sections = addMode ? SECTIONS : SECTIONS.filter(([key]) => key !== 'plan');
    const isRequired = (key) => sections.find(section => section[0] === key)[2] === 'required';
    const stepStatus = (key) => status[key] ? 'done' : tried && isRequired(key) ? 'bad' : '';
    const doneCount = sections.filter(([key]) => status[key]).length;
    const progress = `${Math.round(doneCount / sections.length * 100)}%`;

    const requiredError = (value) => tried && !String(value ?? '').trim() ? t('required_field') : '';
    const numberError = (value, required) => !tried ? ''
        : isEmpty(value) ? (required ? t('required_field') : '')
            : isNumber(value) ? '' : t('number_required');
    const chooseError = (value) => tried && !value ? t('choose_required') : '';

    const jump = (key) => {
        const el = document.getElementById(`nl-${key}`);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 92, behavior: 'smooth' });
    };
    const showMessage = (kind, text) => {
        setMessage({ kind, text });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const validate = () => {
        if (!videoOk) {
            setMessage({ kind: 'error', text: t('video_invalid') });
            setTimeout(() => jump('photos'), 30);
            return false;
        }
        const firstBad = sections.find(([key]) => isRequired(key) && !status[key]);
        if (!firstBad) return true;
        setTried(true);
        const onlyMap = status.basic && status.data && status.contact;
        setMessage({ kind: 'error', text: t(onlyMap ? 'error_lnglat' : numbersOk || isEmpty(form.area) ? 'error_missing_values' : 'error_wrong_type') });
        setTimeout(() => jump(firstBad[0]), 30);
        return false;
    };

    const payload = () => ({
        ...form,
        video_url: String(form.video_url || '').trim(),
        bills: form.bills === "" ? null : form.bills,
        deposit: form.deposit === "" ? null : form.deposit,
        lat: form.lnglat[0],
        lng: form.lnglat[1],
    });

    // uploads the new photos, then saves the gallery order; resolves to whether anything failed
    const uploadFiles = async (id) => {
        const results = await Promise.allSettled(photos.map(image => {
            if (image.uploaded) return Promise.resolve(image.id);
            const data = new FormData();
            data.append("photo", image.file);
            data.append("rotation", image.rotation);
            return FileAPI.postImage(id, data).then(res => res.data.id);
        }));
        let failed = results.some(result => result.status !== 'fulfilled');
        const order = results.filter(result => result.status === 'fulfilled').map(result => result.value);
        if (order.length > 1) {
            await FileAPI.orderImages(id, order).catch(err => { console.log(err); failed = true; });
        }
        return failed;
    };

    const saveChanges = async () => {
        setBusy(true);
        try {
            await ListingAPI.editListing(listingID, payload());
        } catch (err) {
            showMessage('error', t("unspecified_edit_error"));
            setBusy(false);
            return;
        }
        const failed = await uploadFiles(listingID);
        // what was just uploaded now belongs to the listing, so it is read back from the server
        photos.forEach(image => !image.uploaded && URL.revokeObjectURL(image.url));
        ListingAPI.getListingData(listingID).then(res => populateListing(res.data)).catch(err => console.log(err));
        showMessage(failed ? 'error' : 'success', t(failed ? "photo_error" : "edit_success"));
        setBusy(false);
    };

    const publish = async () => {
        setBusy(true);
        let id;
        try {
            id = (await ListingAPI.postListing({ ...payload(), premium: plan === 'premium' })).data.id;
        } catch (err) {
            showMessage('error', t(err.response?.status === 402 ? "low_funds" : "unspecified_insert_error"));
            setBusy(false);
            setDialog(null);
            return;
        }
        const photoError = await uploadFiles(id);
        navigate("/postavljen-novi", { state: { listing: id, photoError } });
    };

    const submit = () => {
        setMessage(null);
        if (!validate()) return;
        if (addMode) setDialog('publish');
        else saveChanges();
    };

    const base = `${api.host}:${api.port}`;
    const typeIndex = (Number(form.listing_type) || 1) - 1;
    const category = listingType.find(item => item.code === typeIndex + 1);
    const priceSuffix = typeIndex === 0 ? '€' : `€ ${t(PRICE_UNITS[typeIndex], { ns: 'home' })}`;
    const propertyTypeLabel = options.types.find(option => option.id === Number(form.property_type))?.label;
    const userTypeLabel = options.userTypes.find(option => option.id === userType)?.label;
    const floorOptions = floor_options.map(option => ({ id: option.id, label: /^\d+$/.test(option.label) ? option.label : t(option.label) }));
    const roomsLabel = num_rooms_options.find(option => option.id === form.num_rooms)?.label;
    const floorLabel = floorOptions.find(option => option.id === form.floor)?.label;
    const maxFloorLabel = max_floor_options.find(option => option.id === form.max_floor)?.label;
    const newCover = photos[0] && !photos[0].uploaded ? photos[0] : null;
    const cover = photos[0] ? (newCover ? newCover.url : `${base}${photos[0].photo}`) : null;
    const previewSpecs = [
        isNumber(form.area) && `${form.area} m²`,
        roomsLabel && `${roomsLabel} ${t('rooms_short', { ns: 'home' })}`,
        floorLabel && `${floorLabel}${maxFloorLabel ? `/${maxFloorLabel}` : ''} ${t('floor_short', { ns: 'home' })}`,
    ].filter(Boolean);
    const isFree = (key) => key === 'standard' && freeStandard;
    // only premium gets a badge on the card; an existing listing shows it while it is promoted
    const previewPlan = (addMode ? plan === 'premium' : form.promoted) ? 'premium' : null;
    const submitLabel =addMode ? t("save") : busy ? t("saving") : t("save_changes");
    const singleChips = (list, key, none) => ({
        options: list.map(option => ({ key: option.id, label: option.label, on: form[key] === option.id })),
        onToggle: (id) => set(key, form[key] === id ? none : id),
        error: chooseError(form[key]),
    });
    const featureChips = (list) => ({
        options: list.map(item => ({ key: item.name, label: t(item.name), on: !!form[item.name] })),
        onToggle: (name) => set(name, !form[name]),
    });
    const number = (key) => sections.findIndex(section => section[0] === key) + 1;
    const section = (key) => ({ id: `nl-${key}`, number: number(key), status: stepStatus(key) });

    return (
        <>
            <div className='hs-nl-form'>
                <main className='hs-nl-main'>
                    <div className='hs-nl-head'>
                        {addMode && <button className='hs-nl-back' onClick={() => navigate('/postavi-oglas')}>← {t("change_category")}</button>}
                        <div className='hs-nl-head-row'>
                            <h1>{addMode ? t("publish", { ns: 'new_listing' }) : t("edit_listing")}</h1>
                            {category && <span className='hs-nl-pill'><CategoryIcon name={category.name} size={14} />{t(category.name, { ns: 'new_listing' })}{propertyTypeLabel && ` · ${propertyTypeLabel}`}</span>}
                            {userTypeLabel && <span className='hs-nl-pill plain'>{userTypeLabel}</span>}
                        </div>
                        <p>{t("form_hint_1")} <strong>*</strong> {t("form_hint_2")}</p>
                    </div>

                    {message &&
                        <div role='alert' className={`hs-nl-alert ${message.kind}`}>
                            {message.kind === 'success'
                                ? <Check size={20} width={2.4} />
                                : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>}
                            {message.text}
                        </div>}

                    <Section {...section('basic')} title={t("basic")} sub={t("basic_sub")}>
                        <div className='hs-nl-grid'>
                            <Field full required label={t("title_label")} placeholder={t("title_placeholder")} maxLength={500}
                                value={form.title} onChange={setText('title')} error={requiredError(form.title)} />
                            <Field full label={t("price")} placeholder={t("price_placeholder")} suffix={priceSuffix} inputMode='decimal'
                                value={form.price} onChange={setNumber('price')} error={numberError(form.price)} hint={t("price_hint")} />
                        </div>
                        <div className='hs-nl-grid'>
                            <Dropdown required label={t("city")} placeholder={t("choose")} searchLabel={t("search")}
                                options={places} value={place?.id} onPick={pickPlace} error={chooseError(form.place)} />
                            <Dropdown required label={t("city_part")} placeholder={place ? t("choose") : t("choose_city_first")} searchLabel={t("search")}
                                options={subplaces} value={subplace?.id} onPick={pickSubplace} disabled={!place} error={place && chooseError(form.subplace)} />
                            <Dropdown required label={t("location")} placeholder={subplace ? t("choose") : t("choose_part_first")} searchLabel={t("search")}
                                options={locations} value={location?.id} onPick={pickLocation} disabled={!subplace} error={subplace && chooseError(form.location)} />
                            <Field full label={t("address")} placeholder={t("address_placeholder")} maxLength={300}
                                value={form.address || ''} onChange={setText('address')} />
                        </div>
                    </Section>

                    <Section {...section('map')} title={`${t("select_location")} *`} sub={t("map_sub")}>
                        <div className={`hs-nl-map ${hasPin ? 'set' : tried ? 'bad' : ''}`}>
                            <LocationPicker position={hasPin ? form.lnglat : null} focus={mapFocus} focusZoom={focusZoom} onPick={(position) => set('lnglat', position)} />
                            <span className='hs-nl-map-chip'>
                                {hasPin
                                    ? <Check size={15} width={2.4} />
                                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>}
                                {hasPin ? t("map_set") : tried ? t("error_lnglat") : t("map_unset")}
                            </span>
                        </div>
                    </Section>

                    <Section {...section('photos')} title={t("media_title")} sub={t("media_sub", { max: MAX_PHOTOS })}
                        action={photoCount > 0 && <button className='hs-nl-text-danger' onClick={() => setDialog('photos')}>{t("delete_all_photos")}</button>}>
                        <input ref={photoInput} type='file' accept={[...PHOTO_TYPES, ...PHOTO_EXTENSIONS].join(',')} multiple hidden
                            onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }} />

                        {photoCount === 0
                            ? <div className={`hs-nl-drop ${dragging ? 'dragging' : ''}`} onClick={() => photoInput.current.click()} {...dragProps}>
                                <span className='hs-nl-drop-icon'><CameraIcon size={26} /></span>
                                <span className='hs-nl-drop-title'>{t("drop_photos")}</span>
                                <span className='hs-nl-drop-text'>{t("drop_or")} <strong>{t("choose_photos").toLowerCase()}</strong> {t("drop_device")} · JPG / PNG / HEIC</span>
                            </div>
                            : <>
                                <div className={`hs-nl-photos ${dragging ? 'dragging' : ''}`} {...dragProps}>
                                    {photos.map((image, index) => (
                                        <div key={image.key} className={`hs-nl-photo ${moving === image.key ? 'moving' : ''}`} {...photoDragProps(image)}
                                            style={image.uploaded ? { backgroundImage: `url(${base}${image.photo})` } : undefined}>
                                            {!image.uploaded && <RotatedPhoto url={image.url} rotation={image.rotation} ratio={4 / 3} />}
                                            {index === 0 && <span className='hs-nl-photo-cover'>{t("cover")}</span>}
                                            {!image.uploaded &&
                                                <button className='rotate' onClick={() => rotatePhoto(image)} title={t("rotate_photo")} aria-label={t("rotate_photo")}><RotateIcon /></button>}
                                            <button onClick={() => removePhoto(image)} title={t("delete_photo")} aria-label={t("delete_photo")}><TrashIcon /></button>
                                        </div>
                                    ))}
                                    {photoCount < MAX_PHOTOS &&
                                        <button className='hs-nl-photo-add' onClick={() => photoInput.current.click()}><span>+</span>{t("add_more")}</button>}
                                </div>
                                <span className='hs-nl-hint'>{t("photo_count", { n: photoCount, max: MAX_PHOTOS })}{photoCount > 1 && ` · ${t("photo_order_hint")}`}</span>
                            </>}

                        <Field type='url' label={t("video_label")} placeholder='https://www.youtube.com/watch?v=…' maxLength={300}
                            value={form.video_url || ''} onChange={setText('video_url')}
                            error={videoOk ? '' : t("video_invalid")} hint={t("video_hint")} />
                    </Section>

                    <Section {...section('data')} title={t("data")} sub={t("data_sub")}>
                        <div className='hs-nl-grid'>
                            <Field required label={t("area")} placeholder='0' suffix='m²' inputMode='decimal'
                                value={form.area ?? ''} onChange={setNumber('area')} error={numberError(form.area, true)} />
                            <Dropdown required label={t("floor")} placeholder={t("choose")} searchLabel={t("search")}
                                options={floorOptions} value={form.floor} onPick={(option) => set('floor', option.id)} error={chooseError(form.floor)} />
                            <Dropdown required label={t("num_floors")} placeholder={t("choose")} searchLabel={t("search")}
                                options={max_floor_options} value={form.max_floor} onPick={(option) => set('max_floor', option.id)} error={chooseError(form.max_floor)} />
                            <Field label={t("bills")} placeholder='0' suffix='RSD' inputMode='decimal'
                                value={form.bills ?? ''} onChange={setNumber('bills')} error={numberError(form.bills)} />
                            <Field label={t("deposit")} placeholder='0' suffix='RSD' inputMode='decimal'
                                value={form.deposit ?? ''} onChange={setNumber('deposit')} error={numberError(form.deposit)} />
                        </div>
                        <ChipGroup required label={t("num_rooms")} {...singleChips(num_rooms_options, 'num_rooms', null)} />
                        <ChipGroup required label={t("num_bathrooms")} {...singleChips(num_bathrooms_options, 'num_bathrooms', null)} />
                        {/* a type picked on the category page is shown in the header instead */}
                        {!(addMode && pickedType) && <ChipGroup required label={t("type")} {...singleChips(sortTypes(options.types), 'property_type', 0)} />}
                        <ChipGroup required label={t("state")} {...singleChips(options.states, 'property_state', 0)} />
                        <ChipGroup check label={t("heating")} note={t("multiple")}
                            options={options.heating.map(option => ({ key: option.value, label: option.name, on: form.heating.includes(option.value) }))}
                            onToggle={(value) => set('heating', form.heating.includes(value) ? form.heating.filter(other => other !== value) : [...form.heating, value])} />
                    </Section>

                    <Section {...section('features')} title={t("features")} sub={t("features_sub")}>
                        <ChipGroup check label={t("additional_information")} {...featureChips(checkboxItems)} />
                        <ChipGroup check label={t("other_information")} {...featureChips(checkboxItemsRestData)} />
                    </Section>

                    <Section {...section('description')} title={t("description")} sub={t("description_sub")}>
                        <label className='hs-nl-desc'>
                            <textarea rows='7' placeholder={t("description_placeholder")} value={form.description || ''} onChange={setText('description')}></textarea>
                            <span className='hs-nl-desc-foot'>
                                <span>{status.description ? t("description_ok") : t("description_hint", { n: MIN_DESCRIPTION })}</span>
                                <span>{(form.description || '').length}</span>
                            </span>
                        </label>
                    </Section>

                    <Section {...section('contact')} title={t("contact")} sub={addMode ? t("contact_sub") : undefined}>
                        <div className='hs-nl-grid'>
                            <Field required type='tel' inputMode='numeric' label={t("phone", { ns: "profile" })} placeholder={t("phone_placeholder", { ns: "profile" })}
                                value={form.phone || ''} onChange={setDigits('phone')} error={requiredError(form.phone)} />
                            <Field type='tel' inputMode='numeric' label={t("additional_phone")} placeholder={t("additional_phone_placeholder")}
                                value={form.mobile || ''} onChange={setDigits('mobile')} />
                            <Field required type='email' label={t("mail", { ns: "profile" })} placeholder={t("mail_placeholder", { ns: "profile" })}
                                value={form.email || ''} onChange={setText('email')}
                                error={requiredError(form.email) || (tried && !emailOk(form.email) ? t("email_invalid") : '')} />
                        </div>
                    </Section>

                    {addMode &&
                        <Section {...section('plan')} title={t("plan_title")} sub={t("plan_sub")}>
                            <div className='hs-nl-plans'>
                                {PLANS.map(key => (
                                    <button type='button' key={key} className={`hs-nl-plan ${plan === key ? 'on' : ''}`} aria-pressed={plan === key} onClick={() => setPlan(key)}>
                                        <span className='hs-nl-plan-head'>
                                            <span className={`hs-nl-plan-badge ${key}`}>{t(`plan_${key}`)}</span>
                                            {plan === key && <Check size={16} width={2.6} />}
                                        </span>
                                        <span className='hs-nl-plan-price'>{isFree(key) ? t("plan_free") : `${formatNumber(planPrices[key])} RSD`}</span>
                                        <span className='hs-nl-plan-text'>{isFree(key) && `${t("plan_free_text")} `}{t(`plan_${key}_text`)}</span>
                                    </button>
                                ))}
                            </div>
                        </Section>}

                </main>

                <aside className='hs-nl-aside'>
                    <div className='hs-nl-preview'>
                        <div className='hs-nl-preview-photo' style={cover && !newCover ? { backgroundImage: `url(${cover})` } : undefined}>
                            {newCover && <RotatedPhoto url={newCover.url} rotation={newCover.rotation} ratio={16 / 10} />}
                            {!cover && <span className='hs-nl-preview-empty'><CameraIcon size={26} />{t("cover_photo")}</span>}
                            <span className='hs-nl-preview-tag'>{t("preview")}{category && ` · ${t(category.name, { ns: 'new_listing' })}`}</span>
                            {previewPlan && <span className={`hs-nl-plan-badge hs-nl-preview-plan ${previewPlan}`}>{t(`plan_${previewPlan}`)}</span>}
                            {photoCount > 0 && <span className='hs-nl-preview-count'>{photoCount} {t('photos', { ns: 'home' })}</span>}
                        </div>
                        <div className='hs-nl-preview-body'>
                            <span className='hs-nl-preview-price'>{isNumber(form.price) && Number(form.price) > 0 ? `${formatNumber(form.price)} ${priceSuffix}` : t("price_placeholder")}</span>
                            <span className={`hs-nl-preview-title ${form.title.trim() ? '' : 'empty'}`}>{form.title.trim() || t("preview_title")}</span>
                            <span className='hs-nl-preview-loc'>{[place?.label, subplace?.label, location?.label].filter(Boolean).join(' · ') || t("location")}</span>
                            {previewSpecs.length > 0 &&
                                <div className='hs-nl-preview-specs'>{previewSpecs.map(spec => <span key={spec}>{spec}</span>)}</div>}
                        </div>
                    </div>
                    <div className='hs-nl-progress'>
                        <div className='hs-nl-progress-head'><span>{t("completeness")}</span><span>{t("done_of", { done: doneCount, total: sections.length })}</span></div>
                        <div className='hs-nl-bar'><div style={{ width: progress }}></div></div>
                        <div className='hs-nl-checklist'>
                            {sections.map(([key, title, weight], index) => (
                                <button key={key} onClick={() => jump(key)}>
                                    <span className={`hs-nl-step ${stepStatus(key)}`}>{status[key] ? <Check size={11} width={3.4} /> : index + 1}</span>
                                    <span className='hs-nl-checklist-label'>{t(title)}</span>
                                    <span className={`hs-nl-checklist-tag ${stepStatus(key) === 'bad' ? 'bad' : ''}`}>{t(`tag_${weight}`)}</span>
                                </button>
                            ))}
                        </div>
                        <button className='hs-nl-publish' disabled={busy} onClick={submit}>{submitLabel}</button>
                        {addMode && freeStandard && <span className='hs-nl-free'>{t("free_note")}</span>}
                    </div>
                </aside>
            </div>

            <div className='hs-nl-mobile-bar'>
                <div className='hs-nl-mobile-progress'>
                    <span>{t("filled_of", { done: doneCount, total: sections.length })}</span>
                    <div className='hs-nl-bar'><div style={{ width: progress }}></div></div>
                </div>
                <button className='hs-nl-publish' disabled={busy} onClick={submit}>{submitLabel}</button>
            </div>

            {dialog === 'publish' &&
                <Confirm title={t("attention", { ns: 'common' })} cancelLabel={t("cancel", { ns: 'common' })}
                    confirmLabel={busy ? t("publishing") : t("confirm_and_publish")} busy={busy}
                    onConfirm={publish} onClose={() => setDialog(null)}>
                    {isFree(plan)
                        ? t("confirm_publish_free")
                        : <>{t(plan === 'premium' ? "confirm_publish_premium" : "confirm_publish_1")}<strong>{formatNumber(planPrices[plan])} </strong>{t("confirm_publish_2")}</>}
                </Confirm>}
            {dialog === 'photos' &&
                <Confirm title={t("attention", { ns: 'common' })} cancelLabel={t("cancel", { ns: 'common' })} confirmLabel={t("confirm", { ns: 'common' })}
                    onConfirm={deleteAllPhotos} onClose={() => setDialog(null)}>
                    {t("all_photos_delete")}
                </Confirm>}
        </>
    );
};
